import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useApolloClient, useMutation } from '@apollo/client';
import { Send, BookOpenText, ThumbsUp, ThumbsDown, CalendarDays, MapPin, User } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { RESEARCH_ASK, RECORD_RESEARCH_FEEDBACK } from '../../graphql';
import { ResearchAskResponse, ResearchHit, ResearchSearchMeta } from '../../types/graphql';
import { SourceCard } from './SourceCard';

interface ChatMessage {
  id: number;
  role: 'user' | 'assistant';
  text: string;
  citations?: ResearchHit[];
  hits?: ResearchHit[];
  followups?: string[];
  searchMeta?: ResearchSearchMeta | null;
  queryId?: string | null;
  feedback?: 'up' | 'down';
  error?: boolean;
}

/** "Interpreting: March 2026 · Manchester · Arteta" — shows how the query was read. */
function SearchMetaChip({ meta }: { meta?: ResearchSearchMeta | null }) {
  if (!meta) return null;
  const items: React.ReactNode[] = [];
  if (meta.dateLabel) {
    items.push(
      <span key="d" className="inline-flex items-center gap-1">
        <CalendarDays className="w-3 h-3" />
        {meta.dateLabel}
      </span>
    );
  }
  for (const loc of meta.locationTerms || []) {
    items.push(
      <span key={`l-${loc}`} className="inline-flex items-center gap-1 capitalize">
        <MapPin className="w-3 h-3" />
        {loc}
      </span>
    );
  }
  for (const name of meta.nameTerms || []) {
    items.push(
      <span key={`n-${name}`} className="inline-flex items-center gap-1">
        <User className="w-3 h-3" />
        {name}
      </span>
    );
  }
  if (items.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-cmp-ink-2">
      <span className="uppercase tracking-wide">Interpreting</span>
      {items}
    </div>
  );
}

interface ResearchChatProps {
  accountId: string;
  lang: string;
}

/**
 * Renders answer text, converting [Cn] citation markers into clickable badges
 * that scroll to the matching source card.
 */
function AnswerText({ text, messageId }: { text: string; messageId: number }) {
  const parts = text.split(/(\[C?\d+\])/g);

  const handleCiteClick = (n: string) => {
    const target = document.getElementById(`msg-${messageId}-source-C${n}`);
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  return (
    <p className="text-sm leading-relaxed text-cmp-ink whitespace-pre-wrap">
      {parts.map((part, i) => {
        const m = part.match(/^\[C?(\d+)\]$/);
        if (m) {
          return (
            <button
              key={i}
              onClick={() => handleCiteClick(m[1])}
              className="inline-block bg-primary text-onfill text-[10px] font-semibold px-1 py-0 rounded align-super mx-0.5 hover:opacity-80 transition-opacity"
            >
              C{m[1]}
            </button>
          );
        }
        return <React.Fragment key={i}>{part}</React.Fragment>;
      })}
    </p>
  );
}

export function ResearchChat({ accountId, lang }: ResearchChatProps) {
  const client = useApolloClient();
  const [recordFeedback] = useMutation(RECORD_RESEARCH_FEEDBACK);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const nextId = useRef(1);
  const scrollRef = useRef<HTMLDivElement>(null);
  // A research session groups this chat's queries for analytics.
  const sessionId = useRef(
    typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : String(Date.now())
  );
  const sessionSeq = useRef(0);

  const sendFeedback = useCallback(
    async (msgId: number, queryId: string | null | undefined, feedback: 'up' | 'down') => {
      if (!queryId) return;
      setMessages((prev) => prev.map((m) => (m.id === msgId ? { ...m, feedback } : m)));
      try {
        await recordFeedback({ variables: { accountId, queryId, feedback } });
      } catch {
        // Non-critical; leave the optimistic state as-is.
      }
    },
    [accountId, recordFeedback]
  );

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const ask = useCallback(
    async (query: string) => {
      const trimmed = query.trim();
      if (!trimmed || loading) return;

      setMessages((prev) => [...prev, { id: nextId.current++, role: 'user', text: trimmed }]);
      setInput('');
      setLoading(true);

      try {
        sessionSeq.current += 1;
        const { data } = await client.query<ResearchAskResponse>({
          query: RESEARCH_ASK,
          variables: {
            accountId,
            query: trimmed,
            lang,
            sessionId: sessionId.current,
            sessionSeq: sessionSeq.current,
          },
          fetchPolicy: 'network-only',
        });

        const result = data.researchAsk;
        const noResults = !result.answer && result.hits.length === 0;
        const retrievalOnly = !result.answer && result.hits.length > 0;

        setMessages((prev) => [
          ...prev,
          {
            id: nextId.current++,
            role: 'assistant',
            text: noResults
              ? 'No results found in the archive. Try a different question.'
              : retrievalOnly
                ? 'Found matching sources in the archive, but answer synthesis is unavailable right now. Check that Platform AI is configured (or your custom AI provider in Settings → Research).'
                : result.answer,
            citations: result.citations,
            hits: result.hits,
            followups: result.followups,
            searchMeta: result.searchMeta,
            // Only offer feedback on a real synthesized answer.
            queryId: noResults || retrievalOnly ? null : result.queryId,
          },
        ]);
      } catch (err: any) {
        setMessages((prev) => [
          ...prev,
          {
            id: nextId.current++,
            role: 'assistant',
            text: err.message || 'Something went wrong. Please try again.',
            error: true,
          },
        ]);
      } finally {
        setLoading(false);
      }
    },
    [accountId, lang, loading, client]
  );

  const isRtl = lang === 'ar';

  return (
    <div className="flex flex-col h-full min-h-0">
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-1 py-4 space-y-6 min-h-0">
        {messages.length === 0 && !loading && (
          <div className="h-full flex flex-col items-center justify-center text-center text-cmp-ink-2 gap-3">
            <BookOpenText className="w-10 h-10 text-teal-700" />
            <p className="max-w-md">
              Ask a question to search the archive of extracted facts and photos. Answers are
              synthesized from indexed content with inline citations.
            </p>
          </div>
        )}

        {messages.map((msg) =>
          msg.role === 'user' ? (
            <div key={msg.id} className="flex justify-end">
              <div
                dir={isRtl ? 'rtl' : 'ltr'}
                className="max-w-[80%] bg-primary text-onfill rounded-2xl rounded-br-sm px-4 py-2.5 text-sm"
              >
                {msg.text}
              </div>
            </div>
          ) : (
            <div key={msg.id} className="flex justify-start">
              <div
                dir={isRtl ? 'rtl' : 'ltr'}
                className="max-w-[95%] w-full bg-core-dark rounded-2xl rounded-bl-sm px-5 py-4 space-y-4"
              >
                {!msg.error && <SearchMetaChip meta={msg.searchMeta} />}

                {msg.error ? (
                  <p className="text-sm text-error">{msg.text}</p>
                ) : (
                  <AnswerText text={msg.text} messageId={msg.id} />
                )}

                {(() => {
                  const sources =
                    msg.citations && msg.citations.length > 0 ? msg.citations : msg.hits || [];
                  if (sources.length === 0) return null;
                  return (
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-wide text-cmp-ink-2 mb-2">
                        Sources
                      </div>
                      <div className="space-y-2">
                        {sources.map((hit, i) => (
                          <SourceCard
                            key={i}
                            hit={hit}
                            anchorId={
                              hit.citationId ? `msg-${msg.id}-source-${hit.citationId}` : undefined
                            }
                            lang={lang}
                          />
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {msg.followups && msg.followups.length > 0 && (
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wide text-cmp-ink-2 mb-2">
                      Suggested questions
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {msg.followups.map((q, i) => (
                        <button
                          key={i}
                          onClick={() => ask(q)}
                          disabled={loading}
                          className="bg-form-dark border border-cmp-line-2 text-cmp-ink text-xs px-3 py-1.5 rounded-full hover:border-primary hover:text-cmp-ink transition-colors disabled:opacity-50"
                        >
                          {q}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {msg.queryId && (
                  <div className="flex items-center gap-2 pt-1 text-cmp-ink-3">
                    <span className="text-[11px]">Was this helpful?</span>
                    <button
                      type="button"
                      aria-label="Helpful"
                      onClick={() => sendFeedback(msg.id, msg.queryId, 'up')}
                      className={`p-1 rounded hover:text-primary transition-colors ${
                        msg.feedback === 'up' ? 'text-primary' : ''
                      }`}
                    >
                      <ThumbsUp className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      aria-label="Not helpful"
                      onClick={() => sendFeedback(msg.id, msg.queryId, 'down')}
                      className={`p-1 rounded hover:text-error transition-colors ${
                        msg.feedback === 'down' ? 'text-error' : ''
                      }`}
                    >
                      <ThumbsDown className="w-3.5 h-3.5" />
                    </button>
                    {msg.feedback && <span className="text-[11px]">Thanks</span>}
                  </div>
                )}
              </div>
            </div>
          )
        )}

        {loading && (
          <div className="flex justify-start">
            <div className="bg-core-dark rounded-2xl rounded-bl-sm px-5 py-4 flex items-center gap-3">
              <div className="w-4 h-4 border-2 border-cmp-line-2 border-t-primary rounded-full animate-spin" />
              <span className="text-sm text-cmp-ink-2">Searching the archive...</span>
            </div>
          </div>
        )}
      </div>

      <div className="flex-shrink-0 pt-3 border-t border-cmp-line">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            ask(input);
          }}
          className="flex gap-2"
        >
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={isRtl ? 'اسأل الأرشيف...' : 'Ask the archive...'}
            dir={isRtl ? 'rtl' : 'ltr'}
            className="flex-1 px-3 py-2 bg-form-dark text-cmp-ink rounded border border-cmp-line-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          />
          <Button
            type="submit"
            disabled={loading || !input.trim()}
            className="bg-button-primary text-button-primary"
            aria-label="Send"
          >
            <Send className="w-4 h-4" />
          </Button>
        </form>
      </div>
    </div>
  );
}
